import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  FileText,
  CheckCircle,
  Shield,
  User,
  Building,
  Hammer,
  List,
  Info,
  Wallet,
  PlusCircle,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

/* ─── Injected styles ─────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

  .cdw * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Plus Jakarta Sans', sans-serif; }

  /* Overlay */
  .cdw-overlay {
    position: fixed; inset: 0; z-index: 50;
    display: flex; align-items: flex-end;
    background: rgba(226,232,240,0.6);
    backdrop-filter: blur(8px);
  }
  @media (min-width: 640px) { .cdw-overlay { align-items: center; justify-content: center; } }

  /* Sheet */
  .cdw-sheet {
    background: #fff;
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-height: 92vh;
    display: flex; flex-direction: column;
    animation: cdw-up 0.28s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 -4px 40px rgba(0,0,0,0.1);
  }
  @media (min-width: 640px) {
    .cdw-sheet { border-radius: 16px; max-width: 560px; max-height: 88vh; box-shadow: 0 8px 48px rgba(0,0,0,0.15); }
  }
  @keyframes cdw-up {
    from { transform: translateY(40px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  /* Drag pill */
  .cdw-pill { width: 36px; height: 4px; background: #E5E7EB; border-radius: 2px; margin: 10px auto 0; }

  /* Header */
  .cdw-header {
    padding: 12px 16px 12px;
    border-bottom: 1px solid #F3F4F6;
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .cdw-header-left { display: flex; align-items: center; gap: 10px; }
  .cdw-header-icon {
    width: 38px; height: 38px; border-radius: 11px;
    background: #EFF6FF;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .cdw-header-title { font-size: 15px; font-weight: 700; color: #111827; letter-spacing: -0.01em; }
  .cdw-header-sub { font-size: 11px; color: #9CA3AF; font-weight: 500; margin-top: 1px; }
  .cdw-close {
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid #E5E7EB; background: #F9FAFB;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #6B7280; transition: all 0.15s;
  }
  .cdw-close:hover { background: #FEF2F2; border-color: #FECACA; color: #EF4444; }

  /* Body scroll */
  .cdw-body { flex: 1; overflow-y: auto; padding: 14px 16px; scrollbar-width: none; }
  .cdw-body::-webkit-scrollbar { display: none; }

  /* Section label */
  .cdw-label {
    font-size: 11px; font-weight: 700; color: #374151;
    text-transform: uppercase; letter-spacing: 0.08em;
    margin-bottom: 8px; display: flex; align-items: center; gap: 5px;
  }
  .cdw-label-req { color: #EF4444; font-size: 10px; }
  .cdw-section { margin-bottom: 16px; }

  /* Date row */
  .cdw-date-row { display: flex; gap: 8px; }
  .cdw-date-input {
    flex: 1; display: flex; align-items: center; gap: 8px;
    background: #F9FAFB; border: 1.5px solid #E5E7EB;
    border-radius: 10px; padding: 9px 12px;
    transition: border-color 0.15s;
  }
  .cdw-date-input:focus-within { border-color: #3B82F6; background: #fff; }
  .cdw-date-input input {
    flex: 1; outline: none; background: transparent;
    font-size: 13px; color: #111827; font-weight: 500;
    font-family: 'JetBrains Mono', monospace;
  }
  .cdw-date-input input::placeholder { color: #9CA3AF; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 400; }
  .cdw-today-btn {
    display: flex; align-items: center; gap: 6px;
    background: #3B82F6; color: #fff;
    border: none; border-radius: 10px; padding: 9px 14px;
    font-size: 12px; font-weight: 700; cursor: pointer;
    transition: background 0.15s; white-space: nowrap;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-today-btn:hover { background: #2563EB; }

  /* Selected place banner */
  .cdw-place-banner {
    display: flex; align-items: center; gap: 10px;
    background: #EFF6FF; border: 1.5px solid #BFDBFE;
    border-radius: 10px; padding: 9px 12px; margin-bottom: 10px;
  }
  .cdw-place-banner-ico {
    width: 32px; height: 32px; border-radius: 8px;
    background: #DBEAFE; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .cdw-place-banner-name { font-size: 13px; font-weight: 700; color: #1D4ED8; }
  .cdw-place-banner-meta { font-size: 10px; color: #6B7280; margin-top: 1px; }
  .cdw-place-banner-tag {
    margin-left: auto; flex-shrink: 0;
    font-size: 9px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 3px 7px; border-radius: 5px;
  }
  .cdw-place-banner-tag.monthly { background: #EDE9FE; color: #7C3AED; }
  .cdw-place-banner-tag.daily { background: #D1FAE5; color: #059669; }
  .cdw-place-banner-tag.global { background: #FEF3C7; color: #D97706; }

  /* Filter tabs */
  .cdw-filter-row { display: flex; gap: 6px; margin-bottom: 10px; }
  .cdw-filter-btn {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 12px; border-radius: 8px; border: 1.5px solid #E5E7EB;
    background: #F9FAFB; font-size: 11px; font-weight: 600; color: #6B7280;
    cursor: pointer; transition: all 0.15s; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-filter-btn.active { background: #3B82F6; border-color: #3B82F6; color: #fff; }
  .cdw-filter-btn:hover:not(.active) { border-color: #93C5FD; color: #3B82F6; }

  /* Horizontal scroll grid */
  .cdw-hscroll { overflow-x: auto; scrollbar-width: none; padding-bottom: 4px; }
  .cdw-hscroll::-webkit-scrollbar { display: none; }
  .cdw-hgrid { display: flex; gap: 8px; width: max-content; }
  .cdw-hcol { display: flex; flex-direction: column; gap: 8px; }

  /* Place card */
  .cdw-place-card {
    width: 130px; border-radius: 10px; padding: 10px;
    border: 1.5px solid #E5E7EB; background: #F9FAFB;
    cursor: pointer; text-align: left; transition: all 0.15s;
    position: relative; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-place-card:hover { border-color: #93C5FD; background: #F0F9FF; }
  .cdw-place-card.sel { border-color: #3B82F6; background: #EFF6FF; box-shadow: 0 2px 8px rgba(59,130,246,0.15); }
  .cdw-place-card-name { font-size: 12px; font-weight: 600; color: #374151; line-height: 1.35; margin-bottom: 6px; padding-right: 18px; min-height: 2.4em; }
  .cdw-place-card.sel .cdw-place-card-name { color: #1D4ED8; }
  .cdw-place-card-tag {
    font-size: 9px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    padding: 2px 6px; border-radius: 4px; display: inline-block;
  }
  .cdw-place-card-tag.perm { background: #DBEAFE; color: #1D4ED8; }
  .cdw-place-card-tag.temp { background: #FEF3C7; color: #D97706; }
  .cdw-check-ico { position: absolute; top: 8px; right: 8px; }

  /* Employee card */
  .cdw-emp-card {
    width: 120px; border-radius: 10px; padding: 10px;
    border: 1.5px solid #E5E7EB; background: #F9FAFB;
    cursor: pointer; text-align: left; transition: all 0.15s;
    position: relative; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-emp-card:hover { border-color: #93C5FD; background: #F0F9FF; }
  .cdw-emp-card.sel { border-color: #3B82F6; background: #EFF6FF; box-shadow: 0 2px 8px rgba(59,130,246,0.15); }
  .cdw-emp-name { font-size: 12px; font-weight: 600; color: #374151; line-height: 1.35; margin-bottom: 6px; padding-right: 18px; min-height: 2.4em; }
  .cdw-emp-card.sel .cdw-emp-name { color: #1D4ED8; }
  .cdw-emp-sal {
    display: flex; align-items: center; gap: 4px;
    background: #fff; border: 1px solid #E5E7EB;
    border-radius: 5px; padding: 2px 6px; width: fit-content;
  }
  .cdw-emp-sal span { font-size: 10px; font-weight: 600; color: #6B7280; font-family: 'JetBrains Mono', monospace; }

  /* Selected count pill */
  .cdw-sel-pill {
    display: inline-flex; align-items: center; gap: 5px;
    background: #EFF6FF; border: 1px solid #BFDBFE;
    border-radius: 20px; padding: 4px 10px; margin-top: 8px;
    font-size: 11px; font-weight: 600; color: #3B82F6;
  }

  /* Employee salary cards */
  .cdw-emp-row-card {
    background: #F9FAFB; border: 1px solid #E5E7EB;
    border-radius: 10px; padding: 10px 12px;
    margin-bottom: 8px;
  }
  .cdw-emp-row-top {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 8px;
  }
  .cdw-emp-row-left { display: flex; align-items: center; gap: 8px; }
  .cdw-emp-num {
    width: 26px; height: 26px; border-radius: 7px;
    background: #3B82F6; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; flex-shrink: 0;
  }
  .cdw-emp-row-name { font-size: 13px; font-weight: 600; color: #111827; }
  .cdw-emp-row-mgr { display: flex; align-items: center; gap: 3px; margin-top: 1px; }
  .cdw-emp-row-mgr span { font-size: 10px; font-weight: 600; color: #7C3AED; }
  .cdw-remove-btn {
    width: 26px; height: 26px; border-radius: 6px; border: none;
    background: #FEE2E2; display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background 0.15s;
  }
  .cdw-remove-btn:hover { background: #FECACA; }

  /* Inputs */
  .cdw-input-row {
    display: flex; align-items: center; gap: 8px;
    background: #fff; border: 1.5px solid #E5E7EB;
    border-radius: 8px; padding: 8px 10px; margin-bottom: 6px;
    transition: border-color 0.15s;
  }
  .cdw-input-row:focus-within { border-color: #3B82F6; }
  .cdw-input-row.purple { background: #FAF5FF; border-color: #DDD6FE; }
  .cdw-input-row.purple:focus-within { border-color: #8B5CF6; }
  .cdw-input-row input, .cdw-input-row textarea {
    flex: 1; outline: none; background: transparent;
    font-size: 13px; color: #111827; resize: none;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-input-row input::placeholder, .cdw-input-row textarea::placeholder { color: #9CA3AF; }
  .cdw-input-currency { font-size: 12px; font-weight: 700; color: #9CA3AF; flex-shrink: 0; }

  /* Manager strip */
  .cdw-mgr-strip {
    display: flex; align-items: center; gap: 10px;
    background: #F5F3FF; border: 1px solid #EDE9FE;
    border-radius: 10px; padding: 10px 12px; margin-top: 10px;
  }
  .cdw-mgr-ava {
    width: 32px; height: 32px; border-radius: 9px;
    background: #EDE9FE; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .cdw-mgr-lbl { font-size: 9px; color: #9CA3AF; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; }
  .cdw-mgr-name { font-size: 13px; font-weight: 700; color: #111827; }
  .cdw-mgr-badge {
    margin-left: auto; display: flex; align-items: center; gap: 4px;
    background: #D1FAE5; border-radius: 6px; padding: 3px 8px;
    font-size: 10px; font-weight: 700; color: #059669;
  }

  /* General note */
  .cdw-note-box {
    display: flex; align-items: flex-start; gap: 8px;
    background: #F9FAFB; border: 1.5px solid #E5E7EB;
    border-radius: 10px; padding: 10px 12px;
    transition: border-color 0.15s;
  }
  .cdw-note-box:focus-within { border-color: #3B82F6; background: #fff; }
  .cdw-note-box textarea {
    flex: 1; outline: none; background: transparent;
    font-size: 13px; color: #111827; resize: none;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-note-box textarea::placeholder { color: #9CA3AF; }

  /* Info banner */
  .cdw-info {
    display: flex; align-items: flex-start; gap: 8px;
    background: #EFF6FF; border: 1px solid #BFDBFE;
    border-radius: 9px; padding: 9px 12px;
    font-size: 12px; color: #1E40AF; font-weight: 500; line-height: 1.5;
  }

  /* Footer */
  .cdw-footer { border-top: 1px solid #F3F4F6; padding: 12px 16px; flex-shrink: 0; }
  .cdw-submit {
    width: 100%; display: flex; align-items: center; justify-content: center; gap: 7px;
    padding: 13px; border-radius: 12px; border: none;
    font-size: 14px; font-weight: 700; cursor: pointer;
    transition: all 0.15s; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .cdw-submit.active { background: #3B82F6; color: #fff; box-shadow: 0 4px 14px rgba(59,130,246,0.35); }
  .cdw-submit.active:hover { background: #2563EB; }
  .cdw-submit.disabled { background: #F3F4F6; color: #9CA3AF; cursor: not-allowed; }
  .cdw-submit-spinner {
    width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff; border-radius: 50%;
    animation: cdw-spin 0.75s linear infinite;
  }
  @keyframes cdw-spin { to { transform: rotate(360deg); } }

  /* Empty states */
  .cdw-empty {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 28px 20px; background: #F9FAFB;
    border: 1.5px dashed #E5E7EB; border-radius: 10px; gap: 6px;
  }
  .cdw-empty p { font-size: 12px; color: #9CA3AF; font-weight: 500; }

  /* Section divider */
  .cdw-divider {
    display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  }
  .cdw-divider-line { flex: 1; height: 1px; background: #F3F4F6; }
  .cdw-divider-txt { font-size: 10px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap; }
`;

export default function BossCreateDayWork({
  visible,
  onClose,
  onSuccess,
  places = [],
  employees = [],
  preselectedDate = "",
  preselectedPlaceId = null,
  preselectedEmployees = [],
}) {
  const { t } = useTranslation();
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlaceType, setSelectedPlaceType] = useState(null);
  const [userData, setUserData] = useState(null);
  const [allData, setAllData] = useState(null);

  const lastLoadedPlaceIdRef = useRef(null);
  const originalEmployeeIdsRef = useRef([]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    const data = localStorage.getItem("allData");
    if (user) setUserData(JSON.parse(user));
    if (data) setAllData(JSON.parse(data));
  }, []);

  const getTodayDate = () => {
    const t = new Date();
    return `${String(t.getDate()).padStart(2, "0")}-${String(t.getMonth() + 1).padStart(2, "0")}-${t.getFullYear()}`;
  };

  const loadEmployeesForPlaceAndDate = (
    placeId,
    selectedDate,
    currentAllData,
  ) => {
    if (!placeId || !selectedDate || !currentAllData) return;
    const workEntries = (currentAllData.myDailyWork || []).filter(
      (w) => w.placeId === placeId && w.date === selectedDate,
    );
    const place = (currentAllData.myPlaces || []).find((p) => p.id === placeId);
    if (workEntries.length > 0) {
      setSelectedEmployees(
        workEntries
          .map((work) => {
            const emp = (currentAllData.myEmployees || []).find(
              (e) => e.id === work.employeeId,
            );
            if (!emp) return null;
            return {
              id: emp.id,
              name: emp.name,
              salary: work.salary?.toString() || emp.salary?.toString() || "",
              note: work.note || "",
              placeDailySalary:
                work.placeDailySalary?.toString() ||
                place?.placeDailySalary?.toString() ||
                "",
            };
          })
          .filter(Boolean),
      );
    } else {
      setSelectedEmployees([]);
    }
    lastLoadedPlaceIdRef.current = placeId;
  };

  useEffect(() => {
    if (visible && allData) {
      const initialDate = preselectedDate || getTodayDate();
      setDate(initialDate);
      setSelectedPlaceId(preselectedPlaceId);
      if (preselectedEmployees && preselectedEmployees.length > 0) {
        const place = (allData.myPlaces || []).find(
          (p) => p.id === preselectedPlaceId,
        );
        const emps = preselectedEmployees.map((emp) => {
          const existing = (allData.myDailyWork || []).find(
            (w) =>
              w.employeeId === emp.id &&
              w.date === initialDate &&
              w.placeId === preselectedPlaceId,
          );
          return {
            id: emp.id,
            name: emp.name,
            salary: emp.salary?.toString() || "",
            note: existing?.note || emp.note || "",
            placeDailySalary:
              existing?.placeDailySalary?.toString() ||
              place?.placeDailySalary?.toString() ||
              "",
          };
        });
        setSelectedEmployees(emps);
        originalEmployeeIdsRef.current = emps.map((e) => e.id);
        lastLoadedPlaceIdRef.current = preselectedPlaceId;
      } else if (preselectedPlaceId) {
        loadEmployeesForPlaceAndDate(preselectedPlaceId, initialDate, allData);
        originalEmployeeIdsRef.current = [];
      } else {
        setSelectedEmployees([]);
        originalEmployeeIdsRef.current = [];
        lastLoadedPlaceIdRef.current = null;
      }
    }
  }, [
    visible,
    preselectedDate,
    preselectedPlaceId,
    preselectedEmployees,
    allData,
  ]);

  useEffect(() => {
    if (
      visible &&
      selectedPlaceId &&
      date &&
      allData &&
      selectedPlaceId !== lastLoadedPlaceIdRef.current
    ) {
      loadEmployeesForPlaceAndDate(selectedPlaceId, date, allData);
    }
  }, [selectedPlaceId, date, visible, allData]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedEmployees([]);
    setDate("");
    setNote("");
    setSelectedPlaceType(null);
    lastLoadedPlaceIdRef.current = null;
    originalEmployeeIdsRef.current = [];
    onClose();
  };

  const toggleEmployee = (employee) => {
    const isSelected = selectedEmployees.find((e) => e.id === employee.id);
    if (isSelected) {
      setSelectedEmployees(
        selectedEmployees.filter((e) => e.id !== employee.id),
      );
    } else {
      setSelectedEmployees([
        ...selectedEmployees,
        {
          id: employee.id,
          name: employee.name,
          salary: employee.salary?.toString() || "",
          note: "",
          placeDailySalary: selectedPlace?.placeDailySalary?.toString() || "",
        },
      ]);
    }
  };

  const updateEmpField = (id, field, val) =>
    setSelectedEmployees(
      selectedEmployees.map((e) => (e.id === id ? { ...e, [field]: val } : e)),
    );
  const removeEmployee = (id) =>
    setSelectedEmployees(selectedEmployees.filter((e) => e.id !== id));

  const handleSubmit = async () => {
    if (!date?.trim()) {
      alert("Please enter a date");
      return;
    }
    if (!selectedPlaceId) {
      alert("Please select a place");
      return;
    }
    const badSal = selectedEmployees.find((e) => !e.salary || isNaN(e.salary));
    if (badSal) {
      alert(`Please enter a valid salary for ${badSal.name}`);
      return;
    }
    if (isPlaceDailyPayment) {
      const badPds = selectedEmployees.find(
        (e) => !e.placeDailySalary || isNaN(e.placeDailySalary),
      );
      if (badPds) {
        alert(`Please enter a valid place daily salary for ${badPds.name}`);
        return;
      }
    }
    if (!userData) {
      alert("User data not found");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        UserId: userData.id,
        PlaceId: selectedPlaceId,
        Date: date,
        Note: note.trim(),
        Employees: selectedEmployees.map((emp) => ({
          EmployeeId: emp.id,
          Salary: parseFloat(emp.salary),
          Note: emp.note || "",
          PlaceDailySalary:
            isPlaceDailyPayment && emp.placeDailySalary
              ? parseFloat(emp.placeDailySalary)
              : null,
        })),
        WhoMake: userData.id,
      };
      const response = await axios.post(
        `${MyDomain}DailyWork/CreateDayWork`,
        payload,
      );
      if (response.status === 200) {
        localStorage.setItem("allData", JSON.stringify(response.data));
        alert("Work entry created successfully!");
        handleClose();
        if (onSuccess) onSuccess(response.data);
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (
        (errorData?.errorType === "EMPLOYEE_CONFLICT" ||
          errorData?.ErrorType === "EMPLOYEE_CONFLICT") &&
        (errorData?.conflicts || errorData?.Conflicts)
      ) {
        const conflicts = errorData.conflicts || errorData.Conflicts;
        let msg = `Employee Conflict on ${errorData.date || errorData.Date || date}:\n\n`;
        conflicts.forEach((c) => {
          msg += `• ${c.employeeName || c.EmployeeName} (at ${c.placeName || c.PlaceName}, added by ${c.addedBy || c.AddedBy})\n`;
        });
        alert(msg);
      } else {
        alert(
          `Error: ${errorData?.message || (typeof errorData === "string" ? errorData : null) || "Failed to create work entry"}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !allData) return null;

  const isUpdateMode = preselectedEmployees.length > 0;
  const allOriginalsRemoved =
    isUpdateMode &&
    originalEmployeeIdsRef.current.length > 0 &&
    originalEmployeeIdsRef.current.every(
      (id) => !selectedEmployees.find((e) => e.id === id),
    );
  const selectedPlace = allData.myPlaces?.find((p) => p.id === selectedPlaceId);
  const isPlaceDailyPayment = selectedPlace?.paidWay === 2;

  const filteredPlaces = (allData.myPlaces || []).filter(
    (p) =>
      p.active === 1 &&
      (selectedPlaceType === null || p.placeType === selectedPlaceType),
  );
  const activeEmployees = (allData.myEmployees || []).filter(
    (e) => e.active === 1,
  );
  const getManagerName = (id) =>
    (allData.myEmployees || []).find((e) => e.id === id)?.name || "Me";

  const toPairs = (arr) => {
    const pairs = [];
    for (let i = 0; i < arr.length; i += 2) pairs.push([arr[i], arr[i + 1]]);
    return pairs;
  };

  const paidWayLabel = { 1: "Monthly", 2: "Daily", 3: "Global" };
  const paidWayClass = { 1: "monthly", 2: "daily", 3: "global" };

  return (
    <>
      <style>{STYLES}</style>
      <div className="cdw-overlay">
        <div className="cdw-sheet" onClick={(e) => e.stopPropagation()}>
          {/* Drag pill (mobile) */}
          <div className="cdw-pill" />

          {/* Header */}
          <div className="cdw-header">
            <div className="cdw-header-left">
              <div className="cdw-header-icon">
                <Calendar size={18} color="#3B82F6" />
              </div>
              <div>
                <p className="cdw-header-title">{t("dayWork")}</p>
                <p className="cdw-header-sub">
                  {isUpdateMode ? t("updateAssignments") : t("createNewEntry")}
                </p>
              </div>
            </div>
            <button className="cdw-close" onClick={handleClose}>
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="cdw-body">
            {/* Date */}
            <div className="cdw-section">
              <p className="cdw-label">
                <Calendar size={11} color="#6B7280" /> {t("date")}{" "}
                <span className="cdw-label-req">*</span>
              </p>
              <div className="cdw-date-row">
                <div className="cdw-date-input">
                  <Calendar size={14} color="#9CA3AF" />
                  <input
                    type="text"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    maxLength={10}
                  />
                </div>
                <button
                  className="cdw-today-btn"
                  onClick={() => setDate(getTodayDate())}
                >
                  <Calendar size={13} /> {t("today")}
                </button>
              </div>
            </div>

            {/* Place */}
            <div className="cdw-section">
              <p className="cdw-label">
                <MapPin size={11} color="#6B7280" /> Place{" "}
                <span className="cdw-label-req">*</span>
              </p>

              {selectedPlace && (
                <div className="cdw-place-banner">
                  <div className="cdw-place-banner-ico">
                    {selectedPlace.placeType === 1 ? (
                      <Building size={15} color="#3B82F6" />
                    ) : (
                      <Hammer size={15} color="#3B82F6" />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      className="cdw-place-banner-name"
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedPlace.place}
                    </p>
                    <p className="cdw-place-banner-meta">
                      {selectedPlace.placeType === 1
                        ? "Permanent"
                        : "Temporary"}
                    </p>
                  </div>
                  {selectedPlace.paidWay && (
                    <span
                      className={`cdw-place-banner-tag ${paidWayClass[selectedPlace.paidWay] || "global"}`}
                    >
                      {paidWayLabel[selectedPlace.paidWay] || "Global"}
                    </span>
                  )}
                </div>
              )}

              <div className="cdw-filter-row">
                {[
                  { label: "All", value: null, Icon: List },
                  { label: "Permanent", value: 1, Icon: Building },
                  { label: "Temporary", value: 2, Icon: Hammer },
                ].map(({ label, value, Icon }) => (
                  <button
                    key={label}
                    className={`cdw-filter-btn${selectedPlaceType === value ? " active" : ""}`}
                    onClick={() => setSelectedPlaceType(value)}
                  >
                    <Icon size={11} /> {label}
                  </button>
                ))}
              </div>

              {filteredPlaces.length === 0 ? (
                <div className="cdw-empty">
                  <Building size={22} color="#D1D5DB" />
                  <p>No places found</p>
                </div>
              ) : (
                <div className="cdw-hscroll">
                  <div className="cdw-hgrid">
                    {toPairs(filteredPlaces).map((pair, ci) => (
                      <div key={ci} className="cdw-hcol">
                        {pair.map(
                          (place) =>
                            place && (
                              <button
                                key={place.id}
                                className={`cdw-place-card${selectedPlaceId === place.id ? " sel" : ""}`}
                                onClick={() => {
                                  setSelectedPlaceId(place.id);
                                  if (place.paidWay === 2)
                                    setSelectedEmployees((prev) =>
                                      prev.map((e) => ({
                                        ...e,
                                        placeDailySalary:
                                          place.placeDailySalary?.toString() ||
                                          "",
                                      })),
                                    );
                                }}
                              >
                                {selectedPlaceId === place.id && (
                                  <div className="cdw-check-ico">
                                    <CheckCircle size={14} color="#3B82F6" />
                                  </div>
                                )}
                                <p className="cdw-place-card-name">
                                  {place.place}
                                </p>
                                <span
                                  className={`cdw-place-card-tag ${place.placeType === 1 ? "perm" : "temp"}`}
                                >
                                  {place.placeType === 1
                                    ? "Permanent"
                                    : "Temporary"}
                                </span>
                              </button>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Employees */}
            <div className="cdw-section">
              <p className="cdw-label">
                <Users size={11} color="#6B7280" /> Employees{" "}
                <span className="cdw-label-req">*</span>
              </p>

              {activeEmployees.length === 0 ? (
                <div className="cdw-empty">
                  <Users size={22} color="#D1D5DB" />
                  <p>No active employees</p>
                </div>
              ) : (
                <div className="cdw-hscroll">
                  <div className="cdw-hgrid">
                    {toPairs(activeEmployees).map((pair, ci) => (
                      <div key={ci} className="cdw-hcol">
                        {pair.map(
                          (emp) =>
                            emp && (
                              <button
                                key={emp.id}
                                className={`cdw-emp-card${selectedEmployees.find((e) => e.id === emp.id) ? " sel" : ""}`}
                                onClick={() => toggleEmployee(emp)}
                              >
                                <div className="cdw-check-ico">
                                  {selectedEmployees.find(
                                    (e) => e.id === emp.id,
                                  ) ? (
                                    <CheckCircle size={14} color="#3B82F6" />
                                  ) : (
                                    <PlusCircle size={14} color="#D1D5DB" />
                                  )}
                                </div>
                                <p className="cdw-emp-name">{emp.name}</p>
                                <div className="cdw-emp-sal">
                                  <DollarSign size={10} color="#9CA3AF" />
                                  <span>{emp.salary} ₪</span>
                                </div>
                              </button>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEmployees.length > 0 && (
                <div className="cdw-sel-pill">
                  <CheckCircle size={12} color="#3B82F6" />
                  {selectedEmployees.length} selected
                </div>
              )}
            </div>

            {/* Salary assignment */}
            {selectedEmployees.length > 0 && (
              <div className="cdw-section">
                <div className="cdw-divider">
                  <div className="cdw-divider-line" />
                  <span className="cdw-divider-txt">
                    Assign Salaries ({selectedEmployees.length})
                  </span>
                  <div className="cdw-divider-line" />
                </div>

                {selectedEmployees.map((emp, idx) => {
                  const existingWork = (allData.myDailyWork || []).find(
                    (w) =>
                      w.employeeId === emp.id &&
                      w.date === date &&
                      w.placeId === selectedPlaceId,
                  );
                  return (
                    <div key={emp.id} className="cdw-emp-row-card">
                      <div className="cdw-emp-row-top">
                        <div className="cdw-emp-row-left">
                          <div className="cdw-emp-num">{idx + 1}</div>
                          <div>
                            <p className="cdw-emp-row-name">{emp.name}</p>
                            {existingWork?.whoMake && (
                              <div className="cdw-emp-row-mgr">
                                <Shield size={10} color="#7C3AED" />
                                <span>
                                  {getManagerName(existingWork.whoMake)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          className="cdw-remove-btn"
                          onClick={() => removeEmployee(emp.id)}
                        >
                          <X size={13} color="#EF4444" />
                        </button>
                      </div>

                      {/* Salary */}
                      <div className="cdw-input-row">
                        <DollarSign size={13} color="#9CA3AF" />
                        <input
                          type="number"
                          value={emp.salary}
                          onChange={(e) =>
                            updateEmpField(emp.id, "salary", e.target.value)
                          }
                          placeholder="Salary"
                        />
                        <span className="cdw-input-currency">₪</span>
                      </div>

                      {/* Place daily salary */}
                      {isPlaceDailyPayment && (
                        <div className="cdw-input-row purple">
                          <Wallet size={13} color="#8B5CF6" />
                          <input
                            type="number"
                            value={emp.placeDailySalary}
                            onChange={(e) =>
                              updateEmpField(
                                emp.id,
                                "placeDailySalary",
                                e.target.value,
                              )
                            }
                            placeholder="Place daily salary"
                          />
                          <span
                            className="cdw-input-currency"
                            style={{ color: "#8B5CF6" }}
                          >
                            ₪
                          </span>
                        </div>
                      )}

                      {/* Note */}
                      <div
                        className="cdw-input-row"
                        style={{ alignItems: "flex-start" }}
                      >
                        <FileText
                          size={13}
                          color="#9CA3AF"
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />
                        <textarea
                          value={emp.note}
                          onChange={(e) =>
                            updateEmpField(emp.id, "note", e.target.value)
                          }
                          placeholder="Note (optional)"
                          rows={2}
                          maxLength={150}
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Manager strip */}
                <div className="cdw-mgr-strip">
                  <div className="cdw-mgr-ava">
                    <User size={15} color="#7C3AED" />
                  </div>
                  <div>
                    <p className="cdw-mgr-lbl">Recording manager</p>
                    <p className="cdw-mgr-name">{userData?.name || "You"}</p>
                  </div>
                  <div className="cdw-mgr-badge">
                    <CheckCircle size={11} /> Active
                  </div>
                </div>
              </div>
            )}

            {/* General note */}
            <div className="cdw-section">
              <p className="cdw-label">
                <FileText size={11} color="#6B7280" /> General Note
              </p>
              <div className="cdw-note-box">
                <FileText
                  size={13}
                  color="#9CA3AF"
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a general note (optional)"
                  rows={2}
                  maxLength={200}
                />
              </div>
            </div>

            {/* Info */}
            {selectedEmployees.length > 0 && !allOriginalsRemoved && (
              <div className="cdw-section">
                <div className="cdw-info">
                  <Info
                    size={14}
                    color="#3B82F6"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  {isUpdateMode
                    ? "Changes will update existing work entries for the selected employees."
                    : `Creating ${selectedEmployees.length} work ${selectedEmployees.length === 1 ? "entry" : "entries"} for ${selectedPlace?.place || "selected place"}.`}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="cdw-footer">
            <button
              className={`cdw-submit${isLoading ? " disabled" : " active"}`}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="cdw-submit-spinner" /> Processing…
                </>
              ) : (
                <>
                  <CheckCircle size={16} />{" "}
                  {isUpdateMode ? "Update Work Entry" : "Create Work Entry"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
